import { useContext, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ActionIcon, Badge, Group, Tooltip } from "@mantine/core";
import {
  IconEdit,
  IconDownload,
  IconTrash,
  IconSend,
} from "@tabler/icons-react";
import { AuthContext } from "../services/context";
import { getCurrentWorkspace } from "../services/userConfig";
import { DataTable } from "../components/DataTable";
import {
  useGetDocumentTemplates,
  useDeleteDocumentTemplate,
} from "../api/user";
import { useDeleteConfirm } from "../hooks/useDeleteConfirm";
import GenerateDocumentModal from "../components/DocumentGenerators/GenerateDocumentModal";
import DistributeDocumentModal from "../components/DocumentGenerators/DistributeDocumentModal";

function DocumentGenerators() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const currentWorkspace = getCurrentWorkspace(user);
  const currentEventId = currentWorkspace.eventId;

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateToDistribute, setTemplateToDistribute] = useState(null);
  const { mutate: deleteTemplate } = useDeleteDocumentTemplate();
  const confirmDelete = useDeleteConfirm("template", deleteTemplate, {
    message:
      "Are you sure you want to delete this template? If documents have already been distributed, they will not be affected.",
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get("page") || "1", 10);
  const limit = 10;

  const setPage = (newPage) => {
    setSearchParams((prev) => {
      prev.set("page", newPage);
      return prev;
    });
  };

  const { data, isPending: isLoading } = useGetDocumentTemplates({
    eventId: currentEventId,
    page,
    limit,
  });

  const columns = [
    {
      accessor: "name",
      title: "Template Name",
    },
    {
      accessor: "distributedTo",
      title: "Distributed to",
      render: (record) =>
        record.isDistributed ? (
          <Badge
            variant="light"
            color={record.distributedToTagColor || undefined}
          >
            {record.distributedToTagTitle ?? "All recipients"}
          </Badge>
        ) : null,
    },
    {
      accessor: "status",
      title: "Status",
      render: (record) =>
        record.isDistributed ? (
          <Badge color="green" variant="light">
            Distributed
          </Badge>
        ) : (
          <Badge color="gray" variant="light">
            Not distributed
          </Badge>
        ),
    },
    {
      accessor: "actions",
      title: "Actions",
      textAlignment: "right",
      width: "0%",
      render: (record) => {
        const isDistributed = !!record.isDistributed;
        return (
          <Group gap="xs" justify="flex-end" wrap="nowrap">
            <Tooltip
              label={
                isDistributed
                  ? "Template already distributed"
                  : "Distribute to recipients"
              }
            >
              <ActionIcon
                variant="light"
                size="md"
                color="blue"
                onClick={() => setTemplateToDistribute(record)}
                disabled={isDistributed}
              >
                <IconSend size={24} />
              </ActionIcon>
            </Tooltip>
            <ActionIcon
              variant="light"
              size="md"
              onClick={() => setSelectedTemplate(record)}
              color="teal"
            >
              <IconDownload size={24} />
            </ActionIcon>
            <ActionIcon
              variant="light"
              size="md"
              onClick={() =>
                navigate(`/app/documents/document-templates/${record.id}`)
              }
            >
              <IconEdit size={24} />
            </ActionIcon>
            <ActionIcon
              variant="light"
              size="md"
              color="red"
              onClick={() => confirmDelete({ id: record.id })}
            >
              <IconTrash size={24} />
            </ActionIcon>
          </Group>
        );
      },
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        records={data?.data}
        fetching={isLoading}
        totalRecords={data?.pagination?.total || 0}
        recordsPerPage={limit}
        page={page}
        onPageChange={setPage}
        noRecordsText="No document templates found"
      />
      <GenerateDocumentModal
        opened={!!selectedTemplate}
        onClose={() => setSelectedTemplate(null)}
        template={selectedTemplate}
      />
      <DistributeDocumentModal
        opened={!!templateToDistribute}
        onClose={() => setTemplateToDistribute(null)}
        template={templateToDistribute}
      />
    </>
  );
}

export default DocumentGenerators;
